/**
 * Flipbook Viewer Component
 * Full-screen flipbook viewer with page navigation, thumbnails, and keyboard support
 * Uses react-pageflip for page flip animations
 */

import React, { useState, useRef, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import HTMLFlipBook from "react-pageflip";
import {
  getFlipbookViewerData,
  incrementViewCount,
} from "../../services/content.service";
import { QUERY_KEYS } from "../../utils/constants";
import LoadingSpinner from "../common/LoadingSpinner";
import Button from "../common/Button";
import type { UUID } from "../../types/common.types";
import type { FlipbookPage } from "../../types/content.types";
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import ChatPanel from "./ChatPanel";

interface FlipbookViewerProps {
  contentItemId: UUID;
  title: string;
  onClose: () => void;
}

// Page component for react-pageflip
const Page = React.forwardRef<
  HTMLDivElement,
  { page: FlipbookPage; loading?: boolean }
>(({ page, loading }, ref) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div ref={ref} className="page bg-white shadow-lg">
      <div className="w-full h-full relative">
        {(!imageLoaded || loading) && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <LoadingSpinner size="sm" />
          </div>
        )}
        <img
          src={page.imageUrl}
          alt={`Page ${page.pageNumber}`}
          className={clsx(
            "w-full h-full object-contain transition-opacity",
            imageLoaded ? "opacity-100" : "opacity-0",
          )}
          onLoad={() => setImageLoaded(true)}
          loading="lazy"
        />
      </div>
    </div>
  );
});

Page.displayName = "Page";

export default function FlipbookViewer({
  contentItemId,
  title,
  onClose,
}: FlipbookViewerProps) {
  const flipBookRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // State
  const [currentPage, setCurrentPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [showThumbnails] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Fetch flipbook data
  const {
    data: flipbookData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [QUERY_KEYS.CONTENT_ITEM, contentItemId, "viewer"],
    queryFn: () => getFlipbookViewerData(contentItemId),
  });

  // Track view on mount
  useEffect(() => {
    incrementViewCount(contentItemId);
  }, [contentItemId]);

  // Keyboard navigation — skip when user is typing in an input/textarea
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const isEditable = (e.target as HTMLElement)?.isContentEditable;
      if (tag === "INPUT" || tag === "TEXTAREA" || isEditable) {
        return;
      }

      switch (e.key) {
        case "ArrowLeft":
        case "PageUp":
          e.preventDefault();
          handlePrevPage();
          break;
        case "ArrowRight":
        case "PageDown":
        case " ":
          e.preventDefault();
          handleNextPage();
          break;
        case "Home":
          e.preventDefault();
          handleFirstPage();
          break;
        case "End":
          e.preventDefault();
          handleLastPage();
          break;
        case "Escape":
          if (isFullscreen) {
            toggleFullscreen();
          } else {
            onClose();
          }
          break;
        case "+":
        case "=":
          e.preventDefault();
          handleZoomIn();
          break;
        case "-":
          e.preventDefault();
          handleZoomOut();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, currentPage]);

  // Fullscreen handling
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Navigation handlers
  const handlePrevPage = useCallback(() => {
    if (flipBookRef.current) {
      flipBookRef.current.pageFlip().flipPrev();
    }
  }, []);

  const handleNextPage = useCallback(() => {
    if (flipBookRef.current) {
      flipBookRef.current.pageFlip().flipNext();
    }
  }, []);

  const handleFirstPage = useCallback(() => {
    if (flipBookRef.current) {
      flipBookRef.current.pageFlip().flip(0);
    }
  }, []);

  const handleLastPage = useCallback(() => {
    if (flipBookRef.current && flipbookData) {
      flipBookRef.current.pageFlip().flip(flipbookData.totalPages - 1);
    }
  }, [flipbookData]);

  const handleGoToPage = useCallback((pageNum: number) => {
    if (flipBookRef.current) {
      flipBookRef.current.pageFlip().flip(pageNum);
    }
  }, []);

  const onPageFlip = useCallback((e: { data: number }) => {
    setCurrentPage(e.data);
  }, []);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + 0.25, 2));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement && containerRef.current) {
      containerRef.current.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  }, []);

  // Download handler
  const handleDownload = useCallback(() => {
    if (flipbookData?.pdfDownloadUrl) {
      window.open(flipbookData.pdfDownloadUrl, "_blank");
    }
  }, [flipbookData]);

  // Chat page jump handler (for citations)
  const handleChatPageJump = useCallback(
    (pageNumber: number) => {
      // Page numbers from citations are 1-based, flipbook uses 0-based index
      handleGoToPage(pageNumber - 1);
    },
    [handleGoToPage],
  );

  // Calculate dimensions - larger base size for better readability
  const getFlipbookDimensions = () => {
    const baseWidth = 550;
    const baseHeight = 750;
    return {
      width: Math.round(baseWidth * zoom),
      height: Math.round(baseHeight * zoom),
    };
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !flipbookData || flipbookData.pages.length === 0) {
    return (
      <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 max-w-md text-center">
          <p className="text-red-600 mb-4">
            {error ? "Failed to load flipbook" : "No pages available"}
          </p>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  const { width, height } = getFlipbookDimensions();
  const totalPages = flipbookData.totalPages;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 bg-gray-900 flex flex-col"
    >
      {/* Header */}
      <div className="flex-none flex items-center justify-between px-4 py-3 bg-gray-800">
        <div className="flex items-center gap-4">
          <h2 className="text-white font-medium truncate max-w-md">{title}</h2>
          <span className="text-gray-400 text-sm">
            Page {currentPage + 1} of {totalPages}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Zoom Controls */}
          <div className="flex items-center gap-1 mr-4">
            <button
              onClick={handleZoomOut}
              disabled={zoom <= 0.5}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title="Zoom out (-)"
            >
              <MagnifyingGlassMinusIcon className="h-5 w-5" />
            </button>
            <span className="text-gray-400 text-sm w-16 text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={handleZoomIn}
              disabled={zoom >= 2}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title="Zoom in (+)"
            >
              <MagnifyingGlassPlusIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Chat */}
          <button
            onClick={() => setIsChatOpen(true)}
            className={clsx(
              "p-2 rounded-lg transition-colors",
              isChatOpen
                ? "bg-blue-600 text-white"
                : "text-gray-400 hover:text-white hover:bg-gray-700",
            )}
            title="Ask questions about this document"
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5" />
          </button>

          {/* Fullscreen */}
          <button
            onClick={toggleFullscreen}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
            title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
          >
            {isFullscreen ? (
              <ArrowsPointingInIcon className="h-5 w-5" />
            ) : (
              <ArrowsPointingOutIcon className="h-5 w-5" />
            )}
          </button>

          {/* Download */}
          <button
            onClick={handleDownload}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
            title="Download PDF"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
          </button>

          {/* Close */}
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
            title="Close (Esc)"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Flipbook Area */}
        <div className="flex-1 flex items-center justify-center relative">
          {/* Navigation Buttons */}
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            className="absolute left-4 z-10 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Previous page (←)"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>

          <button
            onClick={handleNextPage}
            disabled={currentPage >= totalPages - 1}
            className="absolute right-4 z-10 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Next page (→)"
          >
            <ChevronRightIcon className="h-6 w-6" />
          </button>

          {/* Flipbook */}
          <div
            className="flipbook-container relative"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "center center",
            }}
          >
            {/* Gutter shadow overlay */}
            <div
              className="absolute inset-0 pointer-events-none z-10"
              style={{
                background: `
                  linear-gradient(to right,
                    transparent 0%,
                    transparent 45%,
                    rgba(0,0,0,0.15) 48%,
                    rgba(0,0,0,0.3) 49.5%,
                    rgba(0,0,0,0.4) 50%,
                    rgba(0,0,0,0.3) 50.5%,
                    rgba(0,0,0,0.15) 52%,
                    transparent 55%,
                    transparent 100%
                  )
                `,
              }}
            />
            <HTMLFlipBook
              ref={flipBookRef}
              width={550}
              height={750}
              size="stretch"
              minWidth={400}
              maxWidth={1200}
              minHeight={500}
              maxHeight={1400}
              showCover={true}
              mobileScrollSupport={true}
              onFlip={onPageFlip}
              className="flipbook"
              style={{}}
              startPage={0}
              drawShadow={true}
              flippingTime={500}
              usePortrait={false}
              startZIndex={0}
              autoSize={false}
              maxShadowOpacity={1}
              showPageCorners={true}
              disableFlipByClick={false}
              swipeDistance={30}
              clickEventForward={true}
              useMouseEvents={true}
            >
              {flipbookData.pages.map((page) => (
                <Page key={page.pageNumber} page={page} />
              ))}
            </HTMLFlipBook>
          </div>
        </div>

        {/* Thumbnail Strip */}
        {showThumbnails && (
          <div className="w-32 bg-gray-800 border-l border-gray-700 overflow-y-auto">
            <div className="p-2 space-y-2">
              {flipbookData.pages.map((page, index) => (
                <button
                  key={page.pageNumber}
                  onClick={() => handleGoToPage(index)}
                  className={clsx(
                    "w-full aspect-[3/4] rounded overflow-hidden border-2 transition-all",
                    currentPage === index
                      ? "border-blue-500 ring-2 ring-blue-500/50"
                      : "border-transparent hover:border-gray-500",
                  )}
                >
                  <img
                    src={page.imageUrl}
                    alt={`Page ${page.pageNumber}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer Navigation */}
      <div className="flex-none flex items-center justify-center gap-4 px-4 py-3 bg-gray-800">
        <button
          onClick={handleFirstPage}
          disabled={currentPage === 0}
          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
          title="First page (Home)"
        >
          <ChevronDoubleLeftIcon className="h-5 w-5" />
        </button>

        <button
          onClick={handlePrevPage}
          disabled={currentPage === 0}
          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
          title="Previous page (←)"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>

        {/* Page Input */}
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={totalPages}
            value={currentPage + 1}
            onChange={(e) => {
              const page = parseInt(e.target.value, 10) - 1;
              if (page >= 0 && page < totalPages) {
                handleGoToPage(page);
              }
            }}
            className="w-16 px-2 py-1 text-center bg-gray-700 text-white border border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <span className="text-gray-400">of {totalPages}</span>
        </div>

        <button
          onClick={handleNextPage}
          disabled={currentPage >= totalPages - 1}
          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
          title="Next page (→)"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>

        <button
          onClick={handleLastPage}
          disabled={currentPage >= totalPages - 1}
          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
          title="Last page (End)"
        >
          <ChevronDoubleRightIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Keyboard Shortcuts Hint */}
      <div className="absolute bottom-20 left-4 text-xs text-gray-500">
        ← → Navigate | Home/End | +/- Zoom | Esc Close
      </div>

      {/* Chat Panel */}
      <ChatPanel
        contentItemId={contentItemId}
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        onPageJump={handleChatPageJump}
      />
    </div>
  );
}
